import { modelName } from '@dbrain/app-core/model-tasks';
import { Badge, Icons, Menu, MenuItem, MenuLabel, MenuSeparator } from '@dbrain/ui';
import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useModels, type ModelOption } from '../../api/hooks.js';
import { usePlan } from '../../api/plan.js';
import { usePlanOffer } from '../plan/PlanOffer.js';

/**
 * 16c.1 (B2cModelSecici): the chat's model picker on the home page and in a conversation's bar. It lists only what the
 * API says this person may pick (`allowed`), the default first ("Varsayılan" in a conversation); on Ücretsiz, Pro's
 * models follow under "Pro ile" with a lock and a Pro badge, and picking one opens the Pro dialog titled with it. The
 * server refuses any other model whatever is sent, so nothing else is shown at all.
 */
export interface ChatModelMenuProps {
  /** the chosen model; null: the default (the package's, the task's or the person's own) */
  value: string | null;
  onPick: (modelRef: string | null) => void;
  task: 'main' | 'code';
  /** the home page's pill, or the conversation bar's button with its dot */
  variant: 'home' | 'bar';
  /** `/model` opens it */
  open?: boolean;
  /** what follows the list (a conversation's "Bu modeli varsayılan yap") */
  extra?: ReactNode;
  'data-testid': string;
}

const usable = (m: ModelOption) => m.kind === 'chat' && m.enabled && m.availability.ok;

/** "dengeli, her iş için" after "Ücretsiz paketinizde · ": the line's first letter small */
const lower = (text: string, locale: string) => text.charAt(0).toLocaleLowerCase(locale) + text.slice(1);

export function useChatModels(task: 'main' | 'code') {
  const models = useModels();
  const all = models.data?.models ?? [];
  // the chat's list, or a code workspace's (its own task model and the models open to the person)
  const refs = task === 'code' ? models.data?.code?.modelRefs : models.data?.chat?.modelRefs;
  const allowed = all.filter((m) => usable(m) && (refs ? refs.includes(m.id) : m.allowed));
  const locked = all.filter((m) => usable(m) && m.locked && !m.allowed);
  const fallback = models.data?.routes[task]?.modelRef ?? models.data?.routes.main?.modelRef ?? null;
  return { models, all, allowed, locked, fallback, fallbackModel: allowed.find((m) => m.id === fallback) ?? null };
}

export function ChatModelMenu({ value, onPick, task, variant, open, extra, 'data-testid': testId }: ChatModelMenuProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const plan = usePlan();
  const offer = usePlanOffer();
  const { all, allowed, locked, fallback, fallbackModel } = useChatModels(task);
  const holder = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const details = holder.current?.querySelector('details');
    if (!open || !details) return;
    details.open = true;
  }, [open]);
  const chosen = value ? (all.find((m) => m.id === value) ?? null) : fallbackModel;
  // a conversation's model this person may no longer pick (a package that ended): shown, never offered
  const gone = value !== null && chosen !== null && !chosen.allowed;
  const name = chosen ? modelName(chosen) : t('model.defaultShort');
  const free = plan?.id === 'free';
  const line = (m: ModelOption) => {
    const description = m.description ? (free ? lower(m.description, locale) : m.description) : null;
    if (free) return [t('b2cModels.picker.inFree'), description].filter(Boolean).join(' · ');
    return description ?? undefined;
  };
  const mark = (selected: boolean) => (selected ? <Icons.check /> : <span className="ui-icon" />);
  const pickable = allowed.filter((m) => variant === 'home' || m.id !== fallback || value === m.id);
  const others = variant === 'bar' ? pickable.filter((m) => m.id !== fallback) : pickable;
  const showLocked = offer.canOffer && locked.length > 0;
  return (
    <div ref={holder} className={variant === 'home' ? 'chat-model chat-model--home' : 'chat-model model-menu'}>
      <Menu
        align="right"
        label={`${name} — ${t('b2cModels.picker.label')}`}
        triggerClassName={variant === 'home' ? 'chat-model__pill' : 'model-button'}
        trigger={
          <>
            {variant === 'bar' ? <span className={chosen && !gone ? 'model-button__dot' : 'model-button__dot model-button__dot--off'} aria-hidden="true" /> : null}
            {gone ? <Icons.lock size={12} aria-hidden="true" /> : null}
            <span className={variant === 'home' ? 'chat-model__name' : 'model-button__name'}>{name}</span>
            <Icons.chevronDown size={13} />
          </>
        }
        panelClassName="chat-model__panel"
        data-testid={testId}
      >
        <MenuLabel>{t('b2cModels.picker.title')}</MenuLabel>
        {gone && chosen ? (
          <p className="chat-model__gone" role="note" data-testid="model-gone">
            {t('b2cModels.picker.notYours', { model: modelName(chosen) })}
          </p>
        ) : null}
        {variant === 'bar' ? (
          <MenuItem icon={mark(value === null)} hint={fallbackModel ? modelName(fallbackModel) : undefined} onClick={() => onPick(null)} data-testid="model-option-default">
            {t('model.defaultOption')}
          </MenuItem>
        ) : null}
        {others.map((m) => (
          <MenuItem
            key={m.id}
            className="chat-model__item"
            icon={mark(variant === 'home' ? (value ?? fallback) === m.id : value === m.id)}
            onClick={() => onPick(variant === 'home' && m.id === fallback ? null : m.id)}
            data-testid="model-option"
            data-label={modelName(m)}
          >
            <span className="chat-model__text">
              <span className="chat-model__option">{modelName(m)}</span>
              {line(m) ? <span className="chat-model__line">{line(m)}</span> : null}
            </span>
          </MenuItem>
        ))}
        {showLocked ? (
          <>
            <MenuSeparator />
            <MenuLabel>{t('b2cModels.picker.proGroup')}</MenuLabel>
            {locked.map((m) => (
              <MenuItem
                key={m.id}
                className="chat-model__item chat-model__item--locked"
                aria-label={t('b2cModels.picker.locked', { model: modelName(m) })}
                onClick={() => offer.model(modelName(m))}
                data-testid="model-option-locked"
                data-label={modelName(m)}
              >
                <span className="chat-model__text">
                  <span className="chat-model__option">{modelName(m)}</span>
                  {m.description ? <span className="chat-model__line">{m.description}</span> : null}
                </span>
                <span className="chat-model__pro">
                  <Badge tone="accent">
                    <Icons.lock size={11} aria-hidden="true" /> {t('b2cModels.picker.pro')}
                  </Badge>
                </span>
              </MenuItem>
            ))}
            <MenuSeparator />
            <MenuItem className="chat-model__compare" icon={<Icons.skills size={14} />} onClick={() => offer.compare()} data-testid="model-compare">
              {t('b2cModels.picker.more')} · {t('b2cModels.picker.compare')}
            </MenuItem>
          </>
        ) : null}
        {extra}
      </Menu>
    </div>
  );
}
